// Farm Authentication Service - Multi-tenant support

class FarmAuthService {
  constructor() {
    this.currentUser = null;
    this.currentFarm = null;
  }

  // Generate unique farm invitation codes
  generateInvitationCode() {
    return 'FARM_' + Math.random().toString(36).substring(2, 15).toUpperCase();
  }

  // Register new farm with manager account
  async registerFarm(managerData, invitationCode) {
    try {
      // Validate invitation code (in real app, validate against backend)
      const validCodes = this.getValidInvitationCodes();
      if (!validCodes.includes(invitationCode)) {
        throw new Error('Invalid invitation code');
      }

      const farmId = 'farm_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
      
      const farm = {
        id: farmId,
        name: managerData.farmName,
        createdAt: new Date().toISOString(),
        googleDriveConfig: {
          folderId: null,
          isConfigured: false
        },
        settings: {
          location: managerData.location || 'Romania',
          timezone: 'Europe/Bucharest'
        }
      };

      const manager = {
        id: 'user_' + Date.now(),
        farmId: farmId,
        email: managerData.email,
        name: managerData.name,
        role: 'manager',
        permissions: ['all'],
        createdAt: new Date().toISOString(),
        isActive: true
      };

      // Store farm and manager data
      this.saveFarmData(farm);
      this.saveUserData(manager);
      
      // Mark invitation code as used
      this.markInvitationCodeUsed(invitationCode);

      return { farm, user: manager };
    } catch (error) {
      console.error('Farm registration error:', error);
      throw error;
    }
  }

  // Manager creates accounts for workers/accountants
  async createUserAccount(userData, createdByManagerId) {
    try {
      const manager = this.getUserById(createdByManagerId);
      if (!manager || manager.role !== 'manager') {
        throw new Error('Only managers can create user accounts');
      }

      const newUser = {
        id: 'user_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
        farmId: manager.farmId,
        email: userData.email,
        name: userData.name,
        role: userData.role, // 'worker' or 'accountant'
        permissions: this.getDefaultPermissions(userData.role),
        createdAt: new Date().toISOString(),
        createdBy: createdByManagerId,
        isActive: true,
        temporaryPassword: this.generateTemporaryPassword()
      };

      this.saveUserData(newUser);
      return newUser;
    } catch (error) {
      console.error('User creation error:', error);
      throw error;
    }
  }

  // Get default permissions based on role
  getDefaultPermissions(role) {
    const permissions = {
      manager: ['all'],
      worker: [
        'fields.view',
        'fields.edit',
        'tasks.view',
        'tasks.edit',
        'inventory.view',
        'weather.view',
        'worker.interface'
      ],
      accountant: [
        'finances.view',
        'finances.edit',
        'parcels.view',
        'parcels.edit',
        'inventory.view',
        'reports.view'
      ]
    };
    return permissions[role] || [];
  }

  // Login with email and password
  async login(email, password, farmId = null) {
    try {
      const users = this.getAllUsers();
      let user = users.find(u => u.email === email && u.isActive);

      if (!user) {
        throw new Error('User not found or inactive');
      }

      // If farmId is provided, ensure user belongs to that farm
      if (farmId && user.farmId !== farmId) {
        throw new Error('User does not belong to this farm');
      }

      // In real app, verify password hash
      // For demo, we'll use simple validation
      if (user.temporaryPassword && password === user.temporaryPassword) {
        // First time login with temporary password
        user.mustChangePassword = true;
      } else if (!this.verifyPassword(password, user.passwordHash)) {
        throw new Error('Invalid password');
      }

      const farm = this.getFarmById(user.farmId);
      if (!farm) {
        throw new Error('Farm not found');
      }

      this.currentUser = user;
      this.currentFarm = farm;

      // Store session
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem('currentFarm', JSON.stringify(farm));

      return { user, farm };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Check if user has permission
  hasPermission(permission) {
    if (!this.currentUser) return false;
    if (this.currentUser.role === 'manager') return true;
    return this.currentUser.permissions.includes(permission);
  }

  // Get current session
  getCurrentSession() {
    if (this.currentUser && this.currentFarm) {
      return { user: this.currentUser, farm: this.currentFarm };
    }

    // Try to restore from localStorage
    const storedUser = localStorage.getItem('currentUser');
    const storedFarm = localStorage.getItem('currentFarm');

    if (storedUser && storedFarm) {
      this.currentUser = JSON.parse(storedUser);
      this.currentFarm = JSON.parse(storedFarm);
      return { user: this.currentUser, farm: this.currentFarm };
    }

    return null;
  }

  // Logout
  logout() {
    this.currentUser = null;
    this.currentFarm = null;
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentFarm');
    localStorage.removeItem('farmData_' + this.currentFarm?.id);
  }

  // Data storage methods (will be replaced with Google Drive API)
  saveFarmData(farm) {
    const farms = JSON.parse(localStorage.getItem('farms') || '[]');
    const existingIndex = farms.findIndex(f => f.id === farm.id);
    
    if (existingIndex >= 0) {
      farms[existingIndex] = farm;
    } else {
      farms.push(farm);
    }
    
    localStorage.setItem('farms', JSON.stringify(farms));
  }

  saveUserData(user) {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    const existingIndex = users.findIndex(u => u.id === user.id);
    
    if (existingIndex >= 0) {
      users[existingIndex] = user;
    } else {
      users.push(user);
    }
    
    localStorage.setItem('users', JSON.stringify(users));
  }

  getFarmById(farmId) {
    const farms = JSON.parse(localStorage.getItem('farms') || '[]');
    return farms.find(f => f.id === farmId);
  }

  getUserById(userId) {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    return users.find(u => u.id === userId);
  }

  getAllUsers() {
    return JSON.parse(localStorage.getItem('users') || '[]');
  }

  getFarmUsers(farmId) {
    const users = this.getAllUsers();
    return users.filter(u => u.farmId === farmId && u.isActive);
  }

  // Invitation code management
  getValidInvitationCodes() {
    // In real app, these would be managed by admin
    return JSON.parse(localStorage.getItem('validInvitationCodes') || '["FARM_DEMO2024", "FARM_TEST123"]');
  }

  markInvitationCodeUsed(code) {
    const validCodes = this.getValidInvitationCodes();
    const updatedCodes = validCodes.filter(c => c !== code);
    localStorage.setItem('validInvitationCodes', JSON.stringify(updatedCodes));
  }

  generateTemporaryPassword() {
    return 'temp_' + Math.random().toString(36).substring(2, 10);
  }

  verifyPassword(password, hash) {
    // In real app, use proper password hashing (bcrypt)
    return password === 'demo123'; // Demo password
  }
}

export default new FarmAuthService(); 